import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/mock";

/**
 * Seo - centralizes per-page <title>, meta description, canonical URL,
 * Open Graph tags and optional JSON-LD structured data.
 * NOTE: For true server-rendered HTML, add a prerender/SSG step at build time
 * (e.g. react-snap). This component ensures correct tags per route.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} | ${SITE.name}`
    : `${SITE.name} | Best Mattress Store in Fort Lauderdale, FL`;
  const canonical = `${SITE.url}${path === "/" ? "" : path}`;
  const ogImage = image || SITE.storePhotos[1];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
          .filter(Boolean)
          .map((obj, i) => (
            <script key={i} type="application/ld+json">
              {JSON.stringify(obj)}
            </script>
          ))}
    </Helmet>
  );
}

export const localBusinessJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "FurnitureStore",
  name: SITE.name,
  image: SITE.storePhotos[1],
  url: SITE.url,
  telephone: SITE.phone,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.state,
    postalCode: SITE.address.zip,
    addressCountry: "US",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: SITE.rating,
    reviewCount: SITE.reviewCount,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "10:00",
      closes: "19:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday", "Sunday"],
      opens: "11:00",
      closes: "18:00",
    },
  ],
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE.url}${it.path}`,
  })),
});

export const productJsonLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.name,
  image: p.image,
  description: p.description,
  brand: { "@type": "Brand", name: p.brand },
  offers: {
    "@type": "Offer",
    priceCurrency: "USD",
    price: p.price,
    availability: "https://schema.org/InStock",
    url: `${SITE.url}/products/${p.slug}`,
  },
});

export const organizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  logo: SITE.logo,
  telephone: SITE.phone,
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.state,
    postalCode: SITE.address.zip,
    addressCountry: "US",
  },
});

export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: SITE.url,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE.url}/mattresses?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const itemListJsonLd = (products) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: products.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${SITE.url}/products/${p.slug}`,
    name: p.name,
  })),
});

